import { useState } from "react";
import { Tag, Tooltip } from "antd";
import { useTranslation } from "react-i18next";

import { DocumentUploadSource } from "../../../api/graphql/schema.generated";
import {
  getColorByUploadSource,
  getLabelByUploadSource,
  getOriginPresentation,
} from "../../../utils";

// Fixed size so a logo never changes the table row height (SMT-1109).
const LOGO_SIZE = 16;

// antd Tags carry an 8px right margin for sitting next to each other. In a
// centered column that margin is centered along with the tag, so the label
// lands ~4px left of the provider logos and the column header. Drop it.
const TAG_STYLE = { marginRight: 0 };

// Leaf component for the Receipt Overview Source column (SMT-903). Extracted as
// its own component because Slices 3 (filter) and 7 (sort) reuse the same badge.
//
// SMT-1109: when the row carries a `documentOrigin`, that exact origin wins over
// the coarse `uploadSource` - accounting providers render as their logo (name on
// hover), intake channels as a text badge. Rows without an origin keep the
// previous upload-source badge, including "Integration".
export const SourceBadge = ({
  source,
  documentOrigin,
}: {
  source?: DocumentUploadSource | null;
  documentOrigin?: string | null;
}) => {
  const { t } = useTranslation("ReceiptOverview");
  // Keyed by URL, not a plain boolean: a row can re-render with a different
  // provider, and one broken asset must not suppress every later logo in the
  // same badge instance.
  const [failedLogoUrl, setFailedLogoUrl] = useState<string | null>(null);

  const origin = getOriginPresentation(documentOrigin);

  // A missing CDN asset falls back to the provider name as a plain badge rather
  // than leaving a broken image in the cell.
  if (origin?.kind === "provider" && failedLogoUrl !== origin.logoUrl) {
    return (
      <Tooltip title={origin.name}>
        <img
          data-test="sourceBadge"
          src={origin.logoUrl}
          alt={origin.name}
          width={LOGO_SIZE}
          height={LOGO_SIZE}
          style={{ objectFit: "contain", verticalAlign: "middle" }}
          onError={() => setFailedLogoUrl(origin.logoUrl)}
        />
      </Tooltip>
    );
  }

  if (origin?.kind === "provider") {
    return (
      <Tag
        data-test="sourceBadge"
        color={getColorByUploadSource(source)}
        style={TAG_STYLE}
      >
        {origin.name}
      </Tag>
    );
  }

  if (origin?.kind === "channel") {
    return (
      <Tag data-test="sourceBadge" color={origin.color} style={TAG_STYLE}>
        {t(origin.labelKey)}
      </Tag>
    );
  }

  if (origin?.kind === "unknown") {
    return (
      <Tag
        data-test="sourceBadge"
        color={getColorByUploadSource(source)}
        style={TAG_STYLE}
      >
        {origin.label}
      </Tag>
    );
  }

  return (
    <Tag
      data-test="sourceBadge"
      color={getColorByUploadSource(source)}
      style={TAG_STYLE}
    >
      {t(getLabelByUploadSource(source))}
    </Tag>
  );
};
